import {
  INVERTER_POWUR_OS_EQUIPMENT_ID,
  MODULE_POWUR_OS_EQUIPMENT_ID,
} from "@logic/lib/powur-os"

import type { ExtendedWarrantySnapshotDto } from "~/api/dto/extended-warranty.dto"
import type {
  PowurOsPricingAdderDto,
  PowurOsPricingEquipmentDto,
  PowurOsPricingFeesDto,
  PowurOsPricingRebatesDto,
} from "~/api/dto/proposal.dto"

import { formatters } from "../utils"

export type SystemPricingItem = {
  id: number
  name: string
  type: string
  price: number
  total: number
  required?: boolean
  qty: number
  isBaseCost: boolean
  lineType?: number | null
  feeItemId?: number | null
  categoryId?: number
}

export enum PricingSummaryLineItemType {
  MODULE = `MODULE`,
  INVERTER = `INVERTER`,
  ENERGY_BACKUP = `ENERGY_BACKUP`,
  ADDITIONAL_EQUIPMENT = `ADDITIONAL_EQUIPMENT`,
  OPTIMIZER = `OPTIMIZER`,
  EQUIPMENT = `EQUIPMENT`,
  INCENTIVE = `INCENTIVE`,
  HARDCODED_ADDER = `HARDCODED_ADDER`,
  ADDER = `ADDER`,
  MARGIN = `MARGIN`,
  REBATE = `REBATE`,
  WARRANTY = `WARRANTY`,
  DEALER_FEE = `DEALER_FEE`,
  DOWN_PAYMENT = `DOWN_PAYMENT`,
  SALES_TAX = `SALES_TAX`,
  GENERAL_EXCISE_TAX = `GENERAL_EXCISE_TAX`,
  EV_OUTLET = `EV_OUTLET`,
  EV_CHARGER = `EV_CHARGER`,
  ROOFING = `ROOFING`,
  POWUR_LENDER_MARGIN_FEE = `POWUR_LENDER_MARGIN_FEE`,
  FEE = `FEE`,
}

const BASE_SERVICES_LINE_TYPE = 1
const MODULE_ADJUSTMENT_ITEM_ID = 17
const INVERTER_ADJUSTMENT_ITEM_ID = 18

export enum PROPOSAL_USE_TYPES {
  MANUAL = 1,
  REQUIRED = 2,
  INTERNAL = 3,
  NOT_REQUIRED = 4,
}

export function consolidateEquipmentItems(
  items: PowurOsPricingEquipmentDto[],
  adders: PowurOsPricingAdderDto[],
  systemSize: number,
): SystemPricingItem[] {
  // Create a map for quick lookup of items by equipmentId
  const itemMap = new Map<number, PowurOsPricingEquipmentDto>()

  // First pass: populate the map with all items
  items.forEach((item) => {
    itemMap.set(item.equipmentId, { ...item }) // Create a copy to avoid mutation
  })

  // Second pass: roll up child costs to parents
  items.forEach((item) => {
    if (item.parentEquipmentId) {
      const parent = itemMap.get(item.parentEquipmentId)
      if (parent) {
        parent.totalPrice += item.totalPrice
      }
    }
  })

  // Third pass: return only items without parents
  const parentItems = Array.from(itemMap.values()).filter(
    (item) => item.parentEquipmentId === null,
  )

  // Fourth pass: roll up adder costs to parent equipment
  adders.forEach((adder) => {
    if (adder.parentEquipmentId) {
      const parent = itemMap.get(adder.parentEquipmentId)
      if (parent) {
        parent.totalPrice += adder.totalPrice
      }
    }
  })

  return parentItems.map((pi: PowurOsPricingEquipmentDto): SystemPricingItem => {
    return {
      id: pi.equipmentId,
      name: pi.name,
      type: PricingSummaryLineItemType.EQUIPMENT,
      price: Math.round((pi.totalPrice / systemSize) * 100) / 100,
      total: pi.totalPrice,
      required: true,
      qty: pi.qty,
      isBaseCost: true,
      lineType: null,
      categoryId: pi.categoryId,
    }
  })
}

export function splitEquipmentForPowurLine(
  items: PowurOsPricingEquipmentDto[],
  adders: PowurOsPricingAdderDto[],
  systemSize: number,
): {
  equipmentItems: SystemPricingItem[]
  equipmentAsAdders: SystemPricingItem[]
} {
  const allEquipment = consolidateEquipmentItems(items, adders, systemSize)

  const equipmentItems: SystemPricingItem[] = []
  const equipmentAsAdders: SystemPricingItem[] = []

  allEquipment.forEach((item) => {
    const equipmentData = items.find((eq) => eq.equipmentId === item.id)

    if (!equipmentData) {
      equipmentItems.push(item)
      return
    }

    const isModuleOrInverter =
      equipmentData.categoryId === MODULE_POWUR_OS_EQUIPMENT_ID ||
      equipmentData.categoryId === INVERTER_POWUR_OS_EQUIPMENT_ID

    if (isModuleOrInverter) {
      equipmentItems.push(item)
    } else {
      const lineType = item.lineType ?? null
      equipmentAsAdders.push({
        ...item,
        type: PricingSummaryLineItemType.ADDER,
        isBaseCost: lineType === BASE_SERVICES_LINE_TYPE,
        lineType,
      })
    }
  })

  return { equipmentItems, equipmentAsAdders }
}

export function mapPowurOsAddersPricingToSystemPricingLineItem(
  items: PowurOsPricingAdderDto[],
  systemSize: number,
): SystemPricingItem[] {
  return items
    .filter((adder) => !adder.parentEquipmentId)
    .map((item: PowurOsPricingAdderDto): SystemPricingItem => {
      return {
        id: item.adderId,
        name: item.title,
        type: PricingSummaryLineItemType.ADDER,
        price: Math.round((item.totalPrice / systemSize) * 100) / 100,
        total: item.totalPrice,
        required: true,
        qty: item.qty,
        isBaseCost:
          item.proposalTypeId === PROPOSAL_USE_TYPES.REQUIRED ||
          item.lineType === BASE_SERVICES_LINE_TYPE,
        lineType: item.lineType,
      }
    })
}

export function mapPowurOsFeesToSystemPricingLineItem(
  items: PowurOsPricingFeesDto[],
  systemSize: number,
): SystemPricingItem[] {
  return items.map((item, index) => {
    return {
      id: index,
      name: item.name,
      type: PricingSummaryLineItemType.FEE,
      price: Math.round((item.cost / systemSize) * 100) / 100,
      total: item.cost,
      required: true,
      qty: 1,
      isBaseCost: true,
      lineType: null,
      feeItemId: item.id,
    }
  })
}

export function mapPowurOsRebatesToSystemPricingLineItem(
  items: PowurOsPricingRebatesDto[],
  systemSize: number,
): SystemPricingItem[] {
  return items.map((item, index) => {
    return {
      id: index,
      name: item.name,
      type: PricingSummaryLineItemType.REBATE,
      price: Math.round((item.cost / systemSize) * 100) / 100,
      total: item.cost,
      required: true,
      qty: 1,
      isBaseCost: false,
      lineType: null,
      feeItemId: null,
    }
  })
}

export function mapPowurDealerFeeToSystemPricingLineItem(
  dealerFee: number,
  dealerFeeRate: number,
  powurLenderFee: number,
  powurLenderFeeRate: number,
  fundingTypeId: number,
  fundingType: string,
  systemSize: number,
): SystemPricingItem {
  const totalDealerFee = powurLenderFee + dealerFee
  const totalDealerFeeRate = powurLenderFeeRate + dealerFeeRate

  const feeLabel =
    dealerFee > 0 ? `${fundingType} Dealer Fee` : `Powur Finance Fee`

  return {
    id: fundingTypeId,
    name: `${feeLabel} (${formatters.roundMargin(totalDealerFeeRate * 100)}%)`,
    type: PricingSummaryLineItemType.DEALER_FEE,
    price: Math.round((totalDealerFee / systemSize) * 100) / 100,
    total: totalDealerFee,
    isBaseCost: false,
    qty: 1,
    lineType: null,
    feeItemId: null,
  }
}

export function mapPowurOsWarrantyToSystemPricingLineItem(
  warrantyAmount: number,
  warrantySnapshot: Pick<
    ExtendedWarrantySnapshotDto,
    "extendedWarrantyName" | "extendedWarrantyPowurOsId"
  >,
  systemSize: number,
): SystemPricingItem | null {
  const { extendedWarrantyPowurOsId, extendedWarrantyName } = warrantySnapshot

  if (extendedWarrantyPowurOsId == null || extendedWarrantyName == null) {
    return null
  }

  return {
    id: extendedWarrantyPowurOsId,
    name: extendedWarrantyName,
    type: PricingSummaryLineItemType.WARRANTY,
    price: Math.round((warrantyAmount / systemSize) * 100) / 100,
    total: warrantyAmount,
    isBaseCost: false,
    qty: 1,
  }
}

export function filterAdderItems(
  items: SystemPricingItem[],
): SystemPricingItem[] {
  const adderTypes = [
    PricingSummaryLineItemType.ADDER,
    PricingSummaryLineItemType.FEE,
    PricingSummaryLineItemType.REBATE,
    PricingSummaryLineItemType.DEALER_FEE,
    PricingSummaryLineItemType.WARRANTY,
  ]

  const isAdjustmentItem = (item: SystemPricingItem): boolean => {
    if (item.type !== PricingSummaryLineItemType.FEE) {
      return false
    }

    return (
      item.feeItemId === MODULE_ADJUSTMENT_ITEM_ID ||
      item.feeItemId === INVERTER_ADJUSTMENT_ITEM_ID
    )
  }

  return items.filter((item) => {
    if (!adderTypes.includes(item.type as PricingSummaryLineItemType)) {
      return false
    }

    if (isAdjustmentItem(item)) {
      return true
    }

    return item.lineType !== BASE_SERVICES_LINE_TYPE && !item.isBaseCost
  })
}
